import type { DateTime } from "luxon"

import type { AppLocalization } from "../../../modules/localization/appLocalization"
import type { DateTimeFactory } from "../../../modules/localization/useDateTime"
import type { OrderRecurrence, RecurrenceOrder } from "../../../modules/nonAdminUser/reports/home/weekMenuTypes"
import { formatRepeatRule } from "../createOrder/components/drawer/meetingDetails/helpers"
import { parseRRuleToCustomObject } from "../createOrder/components/form/helpers"

export type SeriesOrderRow = Readonly<{
    id: number
    deliveryTime: DateTime
    isLocked: boolean
}>

export function toSeriesOrderRows(
    orders: ReadonlyArray<RecurrenceOrder>,
    dateTimeFactory: DateTimeFactory,
): ReadonlyArray<SeriesOrderRow> {
    return orders
        .map((order) => ({
            id: order.id,
            deliveryTime: dateTimeFactory.fromApi(order.deliveryTime),
            isLocked: Boolean(order.isLocked),
        }))
        .sort((a, b) => a.deliveryTime.toMillis() - b.deliveryTime.toMillis())
}

export function formatRecurrencePattern(
    recurrence: OrderRecurrence,
    l10n: Pick<AppLocalization, "getString">,
    dateTimeFactory: DateTimeFactory,
): string {
    try {
        const values = parseRRuleToCustomObject(recurrence.pattern, dateTimeFactory)
        const rule = formatRepeatRule(values, l10n)

        return values.startDate.isValid ? `${rule} · ${dateTimeFactory.formatTime(values.startDate)}` : rule
    } catch {
        return recurrence.description
    }
}

export function formatSeriesPeriod(
    orders: ReadonlyArray<SeriesOrderRow>,
    dateTimeFactory: DateTimeFactory,
): string | undefined {
    const first = orders.at(0)
    const last = orders.at(-1)
    if (!first || !last) return undefined

    const from = dateTimeFactory.formatDate(first.deliveryTime)

    return first === last ? from : `${from} – ${dateTimeFactory.formatDate(last.deliveryTime)}`
}
